import { useEffect, useRef, useState, type ReactNode } from "react"
import { cn } from "../../lib/utils"
import { Kbd } from "./kbd"

export interface TooltipProps {
  label: string
  /** Keyboard shortcut chip, for example "⌘U". */
  shortcut?: string
  /** Suppress the tooltip (for example, while the trigger's popover is open). */
  disabled?: boolean
  children: ReactNode
  className?: string
}

/**
 * Dark hover/focus tooltip shown above its trigger after a short delay, with an
 * optional keyboard-shortcut chip. Wraps its child in an inline relative span
 * and observes focus/hover on the whole subtree, so it works around any trigger
 * (button, popover) without cloning it.
 */
export const Tooltip = ({
  label,
  shortcut,
  disabled,
  children,
  className,
}: TooltipProps) => {
  const [open, setOpen] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => () => clearTimeout(timer.current), [])

  useEffect(() => {
    if (disabled) {
      clearTimeout(timer.current)
      setOpen(false)
    }
  }, [disabled])

  const show = () => {
    if (disabled) return
    clearTimeout(timer.current)
    timer.current = setTimeout(() => setOpen(true), 450)
  }
  const hide = () => {
    clearTimeout(timer.current)
    setOpen(false)
  }

  return (
    <span
      className="relative inline-flex"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocusCapture={show}
      onBlurCapture={hide}
      onClickCapture={hide}
    >
      {children}
      {open && (
        <span
          role="tooltip"
          className={cn(
            "pointer-events-none absolute bottom-[calc(100%+8px)] left-1/2 z-50 flex -translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded-md bg-foreground px-2 py-1 text-xs font-medium text-background shadow-md",
            className,
          )}
        >
          {label}
          {shortcut && <Kbd keys={shortcut} tone="inverted" />}
        </span>
      )}
    </span>
  )
}
